import { m } from "@plannello/i18n/messages";
import type { ItemLite } from "@plannello/shared";
import { memo } from "react";
import { AvatarStack } from "@/components/domain/AvatarStack";
import { FieldChip, MoreChip } from "@/components/domain/FieldChip";
import { ItemKey } from "@/components/domain/ItemKey";
import { PriorityIcon } from "@/components/domain/PriorityIcon";
import { ProgressRing } from "@/components/domain/ProgressRing";
import { StatusIcon } from "@/components/domain/StatusIcon";
import { TypeIcon } from "@/components/domain/TypeIcon";
import type { Catalog } from "@/data/catalog";
import { TranslatedMark, titleTooltip, useDisplayTitle } from "@/data/titles";
import { cx } from "@/lib/cx";
import { loc } from "@/lib/format";
import { type ChildStats, itemChips } from "./display";

/**
 * Board card: key and assignee on top, the title (two lines max), then a meta row with type,
 * priority, status (when the board is not grouped by status), Field chips and the child rollup.
 * Rendered inside a GridListItem, which owns focus, selection and drag.
 */
export const ItemCard = memo(function ItemCard({
  item,
  catalog,
  stats,
  showStatus,
  className,
}: {
  item: ItemLite;
  catalog: Catalog;
  stats?: ChildStats;
  showStatus: boolean;
  className?: string;
}) {
  const shownTitle = useDisplayTitle(item);
  const type = catalog.typeById.get(item.typeId);
  const status = catalog.statusById.get(item.statusId);
  const chips = itemChips(item, catalog);
  const shown = chips.slice(0, 2);
  const people = item.assigneeIds.map((id) => catalog.userById.get(id)).filter((u) => !!u);
  return (
    <div data-item-id={item.id} className={cx("flex min-w-0 flex-col gap-1.5", className)}>
      <div className="flex h-5 items-center gap-2">
        <ItemKey value={item.key} />
        <span className="flex-1" />
        <AvatarStack people={people} size="sm" />
      </div>
      <p className="line-clamp-2 text-sm leading-5 text-fg text-pretty" title={titleTooltip(shownTitle)}>
        {shownTitle.text}
        <TranslatedMark d={shownTitle} />
      </p>
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        {type ? <TypeIcon name={type.icon} color={type.color} label={loc(type.name)} /> : null}
        {item.priority ? <PriorityIcon value={item.priority} /> : null}
        {showStatus && status ? <StatusIcon category={status.category} label={loc(status.name)} /> : null}
        {stats ? (
          <span className="flex items-center gap-1 text-xs text-fg-subtle tnum">
            <ProgressRing
              done={stats.done}
              total={stats.total}
              label={m.item_children_progress({ done: stats.done, total: stats.total })}
            />
            <span aria-hidden="true">
              {stats.done}/{stats.total}
            </span>
          </span>
        ) : null}
        {shown.map((c) => (
          <FieldChip key={c.id} label={c.label} color={c.color} field={c.field} className="max-w-32" />
        ))}
        {chips.length > shown.length ? (
          <MoreChip
            count={chips.length - shown.length}
            label={chips
              .slice(shown.length)
              .map((c) => c.label)
              .join(", ")}
          />
        ) : null}
      </div>
    </div>
  );
});
